/**
 * Helpers for customer-invoice email composition (own bill vs vendor forward).
 */

export const TEMPLATE_SOURCE = {
    BILLING_ACCOUNT: "billing_account",
    VENDOR_FORWARD: "vendor_forward",
};

/**
 * True when this PDF is a supplier invoice we forward, not our own bill.
 * Mirrors API resolve_customer_invoice_vendor_name / is_vendor_forwarded_invoice.
 *
 * @param {object|null} invoice
 * @param {string|number|null} agentEntityId - current entity / order agent id
 */
export function isVendorForwardedInvoice(invoice, agentEntityId) {
    if (!invoice) {
        return false;
    }
    if (invoice.provider || invoice.provider_data) {
        return true;
    }
    const billerId = invoice.biller ?? invoice.biller_data?.id ?? null;
    if (
        billerId != null &&
        agentEntityId != null &&
        String(billerId) !== String(agentEntityId)
    ) {
        return true;
    }
    return false;
}

export function resolveInvoiceVendorName(invoice) {
    if (!invoice) {
        return "";
    }
    return (
        invoice.provider_data?.name ||
        invoice.provider_data?.entity_data?.name ||
        invoice.biller_data?.name ||
        ""
    );
}

export function classifyInvoices(invoices, agentEntityId) {
    const list = (Array.isArray(invoices) ? invoices : [invoices]).filter(Boolean);
    const own = [];
    const vendor = [];
    for (const invoice of list) {
        if (isVendorForwardedInvoice(invoice, agentEntityId)) {
            vendor.push(invoice);
        } else {
            own.push(invoice);
        }
    }
    return { own, vendor, all: list };
}

export function defaultTemplateSource(invoices, agentEntityId) {
    const { vendor } = classifyInvoices(invoices, agentEntityId);
    return vendor.length > 0
        ? TEMPLATE_SOURCE.VENDOR_FORWARD
        : TEMPLATE_SOURCE.BILLING_ACCOUNT;
}

/**
 * System (non-BA) subject/message for vendor-forward emails.
 * `translate` should accept (key, values) like Vue i18n `$t`.
 */
export function makeVendorForwardInvoiceSubject(invoice, translate) {
    const vendor = resolveInvoiceVendorName(invoice);
    const invoiceId = invoice?.invoice_id || "";
    if (vendor) {
        return translate("vendorForwardSubjectWithVendor", {
            vendor,
            invoice_id: invoiceId,
        });
    }
    return translate("vendorForwardSubject", { invoice_id: invoiceId });
}

export function makeVendorForwardInvoiceMessage(invoice, translate) {
    const vendor =
        resolveInvoiceVendorName(invoice) || translate("theVendor");
    const orderId =
        invoice?.order_data?.order_id ||
        invoice?.order_id ||
        (typeof invoice?.order === "object" ? invoice.order?.order_id : null) ||
        "";
    if (orderId) {
        return translate("vendorForwardMessageWithOrder", {
            vendor,
            order_id: orderId,
        });
    }
    return translate("vendorForwardMessage", { vendor });
}

export function makeVendorForwardOrderSubject(order, invoicesCount, translate) {
    if (invoicesCount > 1) {
        return translate("vendorForwardOrderSubjectPlural", {
            order_id: order?.order_id || "",
        });
    }
    return translate("vendorForwardOrderSubject", {
        order_id: order?.order_id || "",
    });
}

export function makeVendorForwardOrderMessage(order, invoicesCount, translate) {
    if (invoicesCount > 1) {
        return translate("vendorForwardOrderMessagePlural", {
            order_id: order?.order_id || "",
        });
    }
    return translate("vendorForwardOrderMessage", {
        order_id: order?.order_id || "",
    });
}

/** Shared i18n strings for vendor-forward templates (merge into component i18n). */
export const vendorForwardI18n = {
    en: {
        theVendor: "the vendor",
        vendorForwardSubjectWithVendor:
            "Vendor invoice {vendor} n°{invoice_id}",
        vendorForwardSubject: "Vendor invoice n°{invoice_id}",
        vendorForwardMessageWithOrder:
            "Hello,\n\nPlease find attached the invoice issued by {vendor} for order {order_id}.\n\nThis is not our invoice. Payment details are those of the vendor; please refer to the attached PDF.\n\nBest regards.",
        vendorForwardMessage:
            "Hello,\n\nPlease find attached the invoice issued by {vendor}.\n\nThis is not our invoice. Payment details are those of the vendor; please refer to the attached PDF.\n\nBest regards.",
        vendorForwardOrderSubject: "Invoice for order {order_id}",
        vendorForwardOrderSubjectPlural: "Invoices for order {order_id}",
        vendorForwardOrderMessage:
            "Hello,\n\nPlease find attached the invoice for order {order_id}.\n\nThis invoice was issued by a supplier, not by us. Payment details are those of the vendor; please refer to the attached PDF.\n\nBest regards.",
        vendorForwardOrderMessagePlural:
            "Hello,\n\nPlease find attached the invoices for order {order_id}.\n\nSome of these invoices were issued by our suppliers, not by us. Payment details are those of each issuer; please refer to the attached PDFs.\n\nBest regards.",
    },
    fr: {
        theVendor: "le fournisseur",
        vendorForwardSubjectWithVendor:
            "Facture fournisseur {vendor} n°{invoice_id}",
        vendorForwardSubject: "Facture fournisseur n°{invoice_id}",
        vendorForwardMessageWithOrder:
            "Bonjour,\n\nVeuillez trouver ci-joint la facture émise par {vendor} pour la commande {order_id}.\n\nIl ne s'agit pas de notre facture. Les modalités de paiement sont celles du fournisseur ; merci de vous reporter au PDF joint.\n\nCordialement.",
        vendorForwardMessage:
            "Bonjour,\n\nVeuillez trouver ci-joint la facture émise par {vendor}.\n\nIl ne s'agit pas de notre facture. Les modalités de paiement sont celles du fournisseur ; merci de vous reporter au PDF joint.\n\nCordialement.",
        vendorForwardOrderSubject: "Facture pour la commande {order_id}",
        vendorForwardOrderSubjectPlural:
            "Factures pour la commande {order_id}",
        vendorForwardOrderMessage:
            "Bonjour,\n\nVeuillez trouver ci-joint la facture pour la commande {order_id}.\n\nCette facture a été émise par un fournisseur, et non par nous. Les modalités de paiement sont celles du fournisseur ; merci de vous reporter au PDF joint.\n\nCordialement.",
        vendorForwardOrderMessagePlural:
            "Bonjour,\n\nVeuillez trouver ci-joint les factures pour la commande {order_id}.\n\nCertaines de ces factures ont été émises par nos fournisseurs, et non par nous. Les modalités de paiement sont celles de chaque émetteur ; merci de vous reporter aux PDF joints.\n\nCordialement.",
    },
};
